import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { NAV_LINKS, PHONE_TEL, PHONE_DISPLAY } from "@/data/content";

const Logo = ({ className = "h-9 md:h-11" }) => (
  <img
    src="/assets/logo-sopaj.png"
    alt="SOPAJ — Impresa Edile, Nibionno"
    className={`${className} w-auto`}
    width="200"
    height="67"
    fetchPriority="high"
  />
);

export { Logo };

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-40 transition-[background-color,box-shadow,border-color] duration-500 border-b ${
          scrolled
            ? "bg-ivory/85 backdrop-blur-xl border-anthracite/10 shadow-sm"
            : "bg-ivory/60 backdrop-blur-md border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8 flex items-center justify-between h-16 md:h-20">
          <Link to="/" data-testid="header-logo-link" aria-label="SOPAJ — Home">
            <Logo />
          </Link>

          <nav data-testid="desktop-nav" className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={({ isActive }) =>
                  `font-display text-[13px] tracking-[0.14em] uppercase transition-colors duration-300 link-underline ${
                    isActive ? "text-terracotta" : "text-anthracite/80 hover:text-anthracite"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={PHONE_TEL}
              data-testid="header-call-link"
              className="hidden xl:flex items-center gap-2 font-display text-[13px] tracking-wide text-anthracite/80 hover:text-terracotta transition-colors duration-300"
            >
              <Phone size={15} strokeWidth={1.5} />
              {PHONE_DISPLAY}
            </a>
            <Link
              to="/contatti"
              data-testid="header-cta-sopralluogo"
              className="hidden md:inline-flex items-center bg-terracotta hover:bg-terracotta-hover text-ivory font-display text-[13px] tracking-[0.12em] uppercase px-5 py-2.5 transition-colors duration-300"
            >
              Richiedi sopralluogo
            </Link>
            <button
              data-testid="mobile-menu-button"
              onClick={() => setOpen(true)}
              aria-label="Apri menu"
              className="lg:hidden p-2 text-anthracite"
            >
              <Menu size={24} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-50 bg-ivory flex flex-col"
          >
            <div className="flex items-center justify-between h-16 px-5 border-b border-anthracite/10">
              <Logo className="h-9" />
              <button
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                aria-label="Chiudi menu"
                className="p-2 text-anthracite"
              >
                <X size={26} strokeWidth={1.5} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-1">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className={({ isActive }) =>
                      `block font-display text-4xl py-3 tracking-tight transition-colors duration-300 ${
                        isActive ? "text-terracotta" : "text-anthracite"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.5 }}
              className="px-8 pb-10 flex flex-col gap-3"
            >
              <Link
                to="/contatti"
                data-testid="mobile-menu-cta-sopralluogo"
                className="w-full text-center bg-terracotta text-ivory font-display text-sm tracking-[0.12em] uppercase py-4"
              >
                Richiedi un sopralluogo
              </Link>
              <a
                href={PHONE_TEL}
                data-testid="mobile-menu-call"
                className="w-full text-center border border-anthracite/30 text-anthracite font-display text-sm tracking-[0.12em] uppercase py-4"
              >
                Chiama ora — {PHONE_DISPLAY}
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
